import React, { useState, useEffect } from 'react';
import { fetchNozzles, saveNozzle, deleteNozzle, fetchPumps, fetchProducts } from '../../services/api';

const colorDot = {
  Black: '#2d3748',
  Red: '#e53e3e',
  Green: '#2d9e5f',
  Blue: '#1a6fad',
  Yellow: '#f6a623'
};

const emptyForm = {
  nozzleNo: '',
  pump: '',
  product: '',
  hoseLen: '',
  status: 'Active'
};

export default function NozzleConfig({ user }) {
  const companyId = user?.Companyid || user?.company_id || "1";

  const [nozzles, setNozzles] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [editId, setEditId] = useState(null);
  const [filterPump, setFilterPump] = useState('All');

  const [pumps, setPumps] = useState(['All']);
  const [products, setProducts] = useState([]);

  const [form, setForm] = useState({
    pump: '',
    nozzleNo: '',
    product: '',
    hoseLen: '',
    openReading: '',
    status: 'Active',
    color: 'Black'
  });

  const loadInitialData = async () => {
    try {
      const [nozelsRes, pumpsRes, prodsRes] = await Promise.all([
        fetchNozzles(companyId),
        fetchPumps(companyId),
        fetchProducts(companyId)
      ]);

      if (nozelsRes.success) {
        const raw = nozelsRes.Response || [];
        const list = Array.isArray(raw) ? raw : [raw];
        setNozzles(list.map(nz => ({
          id: nz.Nozzleid,
          pump: nz.PumpName,
          nozzleNo: nz.NozzleName,
          product: nz.ProductName,
          hoseLen: Number(nz.Hose_len),
          openReading: 0,
          status: nz.Status,
          color: 'Black'
        })));
      }

      if (pumpsRes.success) {
        const raw = pumpsRes.Response || [];
        const pumpNames = (Array.isArray(raw) ? raw : [raw]).map(p => p.PumpName);
        setPumps(['All', ...pumpNames]);
        if (pumpNames.length > 0 && !form.pump) {
          setForm(prev => ({ ...prev, pump: pumpNames[0] }));
        }
      }

      if (prodsRes.success) {
        const raw = prodsRes.Response || [];
        const productNames = (Array.isArray(raw) ? raw : [raw]).map(p => p.Name);
        setProducts(productNames);
        if (productNames.length > 0 && !form.product) {
          setForm(prev => ({ ...prev, product: productNames[0] }));
        }
      }
    } catch (err) {
      console.error("Nozzle load error:", err);
    }
  };

  useEffect(() => {
    loadInitialData();
  }, [companyId]);

  // =========================================================

  const filtered = filterPump === 'All'
    ? nozzles
    : nozzles.filter(n => n.pump === filterPump);

  const openAdd = () => {

    setEditId(null);

    setForm({
      pump: pumps[1] || '',
      nozzleNo: '',
      product: products[0] || '',
      hoseLen: '',
      openReading: '',
      status: 'Active',
      color: 'Black'
    });

    setShowForm(true);

  };

  const openEdit = (n) => {

    setEditId(n.id);
    setForm({ ...n });
    setShowForm(true);

  };

  const handleSave = async () => {
    try {
      const payload = {
        Companyid: companyId,
        Nozzle_no: form.nozzleNo.replace("N", ""),
        NozzleName: form.nozzleNo,
        PumpName: form.pump,
        Product: form.product,
        Hose_len: String(form.hoseLen),
        Status: form.status,
        IDS: editId ? String(editId) : "0"
      };

      const result = await saveNozzle(payload);

      if (result.success || result.Response === "UPDATED" || result.Response === "INSERTED") {
        await loadInitialData();
        setShowForm(false);
        setEditId(null);
        setForm(emptyForm);
      } else {
        alert(result.message || "Failed to save nozzle");
      }
    } catch (err) {
      console.error("Nozzle API error:", err);
      alert("Something went wrong");
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this nozzle?')) return;
    try {
      const result = await deleteNozzle(id);
      if (result.success || result.Response === "DELETED" || result.status === "Success") {
        await loadInitialData();
      } else {
        alert(result.message || "Failed to delete nozzle");
      }
    } catch (err) {
      console.error("Nozzle Delete error:", err);
    }
  };

  const statusCount = (s) => nozzles.filter(n => n.status === s).length;

  return (

    <div className="pump-page">

      <div className="page-header">

        <h2 className="page-title">Pump/Nozzle Configuration</h2>

        <button
          className="btn btn-primary btn-sm"
          onClick={openAdd}
        >
          ＋ Add Nozzle
        </button>

      </div>

      {/* SUMMARY CARDS */}

      <div
        style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fill,minmax(150px,1fr))',
          gap: 12,
          marginBottom: 14
        }}
      >

        {[
          { label: 'Total Nozzles', val: nozzles.length, color: 'var(--primary)' },
          { label: 'Active', val: statusCount('Active'), color: 'var(--secondary)' },
          { label: 'Inactive', val: statusCount('Inactive'), color: 'var(--text-muted)' },
          
        ].map(c => (

          <div
            key={c.label}
            style={{
              background: '#fff',
              border: '1px solid var(--border)',
              borderRadius: 'var(--radius)',
              padding: '12px 16px',
              boxShadow: 'var(--shadow-sm)',
              borderLeft: `4px solid ${c.color}`
            }}
          >

            <div style={{ fontSize: 22, fontWeight: 800, color: c.color }}>
              {c.val}
            </div>

            <div style={{ fontSize: 11.5, color: 'var(--text-secondary)', fontWeight: 600 }}>
              {c.label}
            </div>

          </div>

        ))}

      </div>

      {/* ADD / EDIT FORM */}

      {showForm && (

        <div className="section-card">

          <div className="section-header">
            {editId ? "Edit Nozzle" : "Add Nozzle"}
          </div>

          <div className="form-row">

            <div className="form-group">
              <label>Pump*</label>
              <select
                className="form-control"
                value={form.pump}
                onChange={e => setForm({ ...form, pump: e.target.value })}
              >
                {pumps.filter(p => p !== 'All').map(p => (
                  <option key={p}>{p}</option>
                ))}
              </select>
            </div>

            <div className="form-group">
              <label>Nozzle No*</label>
              <input
                className="form-control"
                value={form.nozzleNo}
                onChange={e => setForm({ ...form, nozzleNo: e.target.value })}
              />
            </div>

            <div className="form-group">
              <label>Product*</label>
              <select
                className="form-control"
                value={form.product}
                onChange={e => setForm({ ...form, product: e.target.value })}
              >
                {products.map(p => (
                  <option key={p}>{p}</option>
                ))}
              </select>
            </div>

            <div className="form-group">
              <label>Hose Length*</label>
              <input
                type="number"
                className="form-control"
                value={form.hoseLen}
                onChange={e => setForm({ ...form, hoseLen: e.target.value })}
              />
            </div>

            <div className="form-group">
              <label>Status*</label>
              <select
                className="form-control"
                value={form.status}
                onChange={e => setForm({ ...form, status: e.target.value })}
              >
                <option>Active</option>
                <option>Inactive</option>
                
              </select>
            </div>

          </div>

          <div className="action-bar" style={{ padding: '0 16px 14px' }}>

            <button className="btn btn-primary" onClick={handleSave}>
              {editId ? "Update" : "Save"}
            </button>

            <button
              className="btn btn-secondary"
              onClick={() => setShowForm(false)}
            >
              Cancel
            </button>

          </div>

        </div>

      )}
 <div className="table-wrapper">

          <table className="data-table">

            <thead>
              <tr>
                <th className="text-left">Pump</th>
                <th className="text-center">Nozzle</th>
                <th className="text-left">Product</th>
                <th className="text-center">Hose (m)</th>
                <th className="text-center">Status</th>
                <th className="text-center">Action</th>
              </tr>
            </thead>

            <tbody>

              {filtered.map(n => (

                <tr key={n.id}>
                  <td className="text-left" style={{ fontWeight: 700 }}>{n.pump}</td>
                  <td className="text-center" style={{ fontFamily: 'monospace', fontWeight: 600 }}>{n.nozzleNo}</td>
                  <td className="text-left">
                    <span className="badge badge-info">{n.product}</span>
                  </td>
                  <td className="text-center">{Number(n.hoseLen || 0).toFixed(1)}</td>
                  <td className="text-center">
                    <span className={`badge ${
                      n.status === 'Active'
                        ? 'badge-success'
                        : n.status === 'Faulty'
                        ? 'badge-danger'
                        : 'badge-warning'
                    }`}>
                      {n.status}
                    </span>
                  </td>
                  <td className="text-center">
                    <button className="icon-btn" onClick={() => openEdit(n)} title="Edit">
                      <i className="bi bi-pencil-square"></i>
                    </button>
                    </td>
                </tr>

              ))}

            </tbody>

          </table>

        </div>
      {/* TABLE remains unchanged */}

    </div>

  );
}